package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.ExperimentalCoroutinesApi
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ClockOutcome
import com.evoapps.evoshift.location.InMemoryLocationNoticeStore
import com.evoapps.evoshift.location.LocationNoticeStore
import com.evoapps.evoshift.domain.model.LocationReading
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.shift.ClockInClockOutUseCase
import com.evoapps.evoshift.domain.usecase.shift.ClockStatus
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flow
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

/**
 * The signed-in person's own shifts as a dashboard shows them, for employees and managers alike:
 * the clock-in button and the next shift. Follows the shifts live and the time every minute, so
 * "clock in" appears an hour before a shift without reopening the screen.
 */
class MyShiftClock(
    private val shiftRepository: ShiftRepository,
    private val clockInClockOut: ClockInClockOutUseCase,
    private val now: () -> Instant = { AppClock.now() },
    private val ticks: () -> Flow<Unit> = ::minuteTicks,
    /** The plan of the clocked shift's workplace: no clocking once it ended (SH-65). */
    private val plans: ObservePlanActiveUseCase? = null,
    /** SH-106: whether this device already explained why the location is read. */
    private val locationNotice: LocationNoticeStore = InMemoryLocationNoticeStore()
) {
    data class State(val clock: ClockStatus = ClockStatus.Idle, val nextShift: Shift? = null)

    /**
     * [workplaceId] narrows the next shift to that workplace; the clock covers every workplace,
     * except one whose plan ended (read-only, SH-65): there it offers nothing.
     */
    @OptIn(ExperimentalCoroutinesApi::class)
    fun observe(userId: String, workplaceId: String? = null): Flow<State> = observeShifts(userId, workplaceId).flatMapLatest { state ->
        val shift = when (val clock = state.clock) {
            is ClockStatus.CanClockIn -> clock.shift
            is ClockStatus.Working -> clock.shift
            else -> null
        }
        if (shift == null) flowOf(state)
        else plans.activeAt(RosterIds.workplaceIdOf(shift.rosterId)).map { active -> if (active) state else state.copy(clock = ClockStatus.Idle) }
    }

    private fun observeShifts(userId: String, workplaceId: String?): Flow<State> {
        val today = now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        // From yesterday: an overnight shift may still be running.
        val since = today.minus(DatePeriod(days = 1)).toString()
        // SH-134: the shifts they cover for a colleague (an unplanned clock-in) are theirs to clock.
        val covered = shiftRepository.observeCoveredShifts(userId)
        return combine(shiftRepository.observeShiftsForUser(userId, since), covered, ticks()) { own, covering, _ ->
            val nowLocal = now().toLocalDateTime(TimeZone.currentSystemDefault()).toString()
            val shifts = (own + covering.filter { it.date >= since }).distinctBy { it.id }
            State(
                clock = clockInClockOut.status(shifts, userId),
                nextShift = shifts
                    .filter { it.isPublished && it.plannedTime.start > nowLocal }
                    .filter { workplaceId == null || RosterIds.workplaceIdOf(it.rosterId) == workplaceId }
                    .minByOrNull { it.plannedTime.start }
            )
        }
    }

    /**
     * Clocks in or out, whichever [status] offers (or, SH-116, starts or ends a break). [readLocation] reads the device's position once
     * and is called only where the shift's workplace has a geofence (SH-106).
     */
    suspend fun click(
        status: ClockStatus,
        userId: String,
        readLocation: (suspend () -> LocationReading)? = null,
        /** SH-116: the break button: while working, start a break or end the one going on. */
        breakButton: Boolean = false
    ): Result<ClockOutcome> = when (status) {
        is ClockStatus.CanClockIn -> if (breakButton) Result.success(ClockOutcome()) else clockInClockOut.clockIn(status.shift.id, userId, readLocation)
        is ClockStatus.Working -> when {
            !breakButton -> clockInClockOut.clockOut(status.shift.id, userId, readLocation)
            status.onBreak -> clockInClockOut.endBreak(status.shift.id, userId, readLocation)
            else -> clockInClockOut.startBreak(status.shift.id, userId, readLocation)
        }
        else -> Result.success(ClockOutcome())
    }

    /**
     * SH-165: clocking out [status]'s shift now ends it well before its planned end (more than
     * ClockRules.EARLY_OUT_MINUTES): the dashboard asks first. Breaks never ask.
     */
    fun isEarlyClockOut(status: ClockStatus): Boolean =
        status is ClockStatus.Working && clockInClockOut.isEarlyClockOut(status.shift)

    /** SH-165: the Undo of a clock-out a moment ago: the shift is open again. */
    suspend fun undoClockOut(shiftId: String, userId: String): Result<Unit> = clockInClockOut.undoClockOut(shiftId, userId)

    /**
     * SH-134: clocks in at [workplaceId] with no shift of one's own now (covering a colleague, or
     * a new shift, both pending a manager).
     */
    suspend fun clockInWithoutShift(workplaceId: String, readLocation: (suspend () -> LocationReading)? = null): Result<ClockOutcome> =
        clockInClockOut.clockInWithoutShift(workplaceId, readLocation)

    /** SH-134: the location notice before a clock-in without a shift at [workplaceId]. */
    suspend fun needsLocationNoticeAt(workplaceId: String): Boolean =
        !locationNotice.isAcknowledged() && runCatching { clockInClockOut.geofenceAt(workplaceId) != null }.getOrDefault(false)

    /** SH-106: the purpose of the location reading must be explained first (once per device). */
    suspend fun needsLocationNotice(status: ClockStatus): Boolean =
        !locationNotice.isAcknowledged() && needsLocation(status)

    fun acknowledgeLocationNotice() = locationNotice.acknowledge()

    /** SH-106: whether clocking [status]'s shift reads the location (its workplace has a geofence). */
    suspend fun needsLocation(status: ClockStatus): Boolean {
        val shift = when (status) {
            is ClockStatus.CanClockIn -> status.shift
            is ClockStatus.Working -> status.shift
            else -> return false
        }
        return runCatching { clockInClockOut.geofenceOf(shift) != null }.getOrDefault(false)
    }
}

/**
 * SH-165: the clock-out just done, until its snackbar ("Clocked out · Undo") is gone; [at] tells
 * two clock-outs of the same shift apart.
 */
data class ClockOutUndo(val shiftId: String, val at: Long = AppClock.now().toEpochMilliseconds())

/** SH-165: "HH:mm" of a local ISO date-time ("2026-02-02T22:00:00" -> "22:00"). */
internal fun clockTimeOf(localDateTime: String): String = localDateTime.substringAfter('T').take(5)

private fun minuteTicks() = flow {
    while (true) {
        emit(Unit)
        delay(60_000)
    }
}
